/**
 * Canonical first-party session lifecycle foundation.
 * Product adapters provide storage, verification, event names and callbacks.
 */
export function createSessionLifecycle({
  channelName,
  unauthorizedEvent,
  verifySession,
  hasLocalSession,
  clearSession,
  minIntervalMs = 15000,
}) {
  let lastVerifiedAt = 0;
  let inFlight = null;
  let channel = null;

  const eventKey = `${channelName}.event`;
  const getChannel = () => {
    if (typeof window === "undefined" || !("BroadcastChannel" in window)) return null;
    if (!channel) channel = new BroadcastChannel(channelName);
    return channel;
  };

  const publish = (type, detail = {}) => {
    const message = { type, detail, at: Date.now() };
    try { getChannel()?.postMessage(message); } catch (_) {}
    try { window.localStorage.setItem(eventKey, JSON.stringify(message)); } catch (_) {}
    return message;
  };

  const revalidate = async ({ force = false } = {}) => {
    if (!hasLocalSession()) return { status: "inactive", reason: "missing_local_session" };
    const now = Date.now();
    if (!force && now - lastVerifiedAt < minIntervalMs) {
      return { status: "skipped", reason: "throttled" };
    }
    if (inFlight) return inFlight;
    inFlight = Promise.resolve(verifySession())
      .then((result) => {
        lastVerifiedAt = Date.now();
        if (result?.status === "inactive") publish("signed-out", { reason: result.reason });
        return result;
      })
      .finally(() => { inFlight = null; });
    return inFlight;
  };

  const install = ({ onActive, onInactive, onUnverifiable } = {}) => {
    let disposed = false;
    const handle = (result) => {
      if (disposed || !result || result.status === "skipped") return;
      if (result.status === "active") onActive?.(result);
      else if (result.status === "unverifiable") onUnverifiable?.(result);
      else onInactive?.(result);
    };
    const check = (force = false) => revalidate({ force }).then(handle);
    const onFocus = () => check(false);
    const onVisibility = () => { if (document.visibilityState === "visible") check(false); };
    const onStorage = (event) => {
      if (event.key === eventKey || event.key === "refreshToken" || event.key === "user") check(true);
    };
    const onMessage = (event) => {
      if (event?.data?.type !== "signed-out") return;
      clearSession();
      onInactive?.({ status: "inactive", reason: event.data?.detail?.reason || "cross_tab_logout" });
    };
    const onUnauthorized = () => check(true);
    const bc = getChannel();
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);
    window.addEventListener(unauthorizedEvent, onUnauthorized);
    document.addEventListener("visibilitychange", onVisibility);
    bc?.addEventListener("message", onMessage);
    return () => {
      disposed = true;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(unauthorizedEvent, onUnauthorized);
      document.removeEventListener("visibilitychange", onVisibility);
      bc?.removeEventListener("message", onMessage);
    };
  };

  return { install, publish, revalidate };
}
